import { useState } from 'react';
import {
  X,
  User,
  Mail,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import {
  ALL_PERMISSIONS,
  getPermissionsByRole,
  type PermissionKey,
  type PermissionMap,
} from '../../auth/permissions';
import { ListAvatar } from '../../components/admin/list-avatar';
import './responsavel-ficha-desktop.css';
import { ROUTES } from '../../api/routes';
import { InviteStatusCard } from '../../components/invite';

const PERFIL_TO_CARGO: Record<string, string> = {
  DIRETOR: 'Diretor(a)',
  VICE_DIRETOR: 'Vice Diretor',
  'VICE-DIRETOR': 'Vice Diretor',
  COORDENACAO: 'Coordenador(a)',
  SECRETARIA: 'Secretário(a)',
};

const PERMISSION_GROUPS: { title: string; items: { key: PermissionKey; label: string }[] }[] = [
  { title: 'Equipe e Configuração', items: [{ key: 'equipe.view', label: 'Visualizar equipe' }, { key: 'equipe.manage', label: 'Gerenciar equipe' }] },
  { title: 'Acadêmico', items: [{ key: 'turnos.view', label: 'Turnos' }, { key: 'materias.view', label: 'Matérias' }, { key: 'turmas.view', label: 'Turmas' }] },
  { title: 'Pessoas', items: [{ key: 'professores.view', label: 'Professores' }, { key: 'alunos.view', label: 'Alunos' }, { key: 'responsaveis.view', label: 'Responsáveis' }] },
  { title: 'Operacional', items: [{ key: 'chamadas.view', label: 'Chamadas' }, { key: 'comunicados.view', label: 'Comunicados' }, { key: 'acesso.view', label: 'Acesso' }] },
];

function resolveRole(u: { perfil?: string; cargo?: string }): string {
  if (PERFIL_TO_CARGO[u.perfil || '']) return u.perfil!;
  if (PERFIL_TO_CARGO[u.cargo || '']) return u.cargo!;
  if (typeof u.cargo === 'string' && ['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO', 'SECRETARIA'].includes(u.cargo)) return u.cargo;
  return u.perfil || 'ESCOLA';
}

function buildPermissionMap(role: string, cargo: string, source?: { preferencias?: { permissoes?: PermissionMap } }): PermissionMap {
  const base = getPermissionsByRole('ESCOLA', role);
  const custom = source?.preferencias?.permissoes;
  const merged = { ...base };
  if (custom && typeof custom === 'object') {
    for (const p of ALL_PERMISSIONS) {
      if (typeof custom[p] === 'boolean') merged[p] = custom[p];
    }
  }
  if (cargo) {
    const byCargo = getPermissionsByRole('ESCOLA', role);
    for (const p of ALL_PERMISSIONS) {
      if (merged[p] === undefined) merged[p] = byCargo[p];
    }
  }
  return merged;
}

export function EquipeFichaFooter({
  membro,
  canManage,
  onEdit,
  onDeactivate,
  onReactivate,
}: {
  membro: { criadoEm?: string; ativo: boolean };
  canManage: boolean;
  onEdit: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
}) {
  return (
    <div className="resp-ficha-foot">
      <span className="resp-ficha-foot-meta">
        {membro.criadoEm
          ? `Cadastrado em ${new Date(membro.criadoEm).toLocaleDateString('pt-BR')}`
          : '—'}
      </span>
      <span className="resp-ficha-foot-spacer" />
      <div className="resp-ficha-foot-actions">
        {canManage && (
          <button type="button" className="resp-ficha-btn resp-ficha-btn--primary" onClick={onEdit}>
            Editar
          </button>
        )}
        {membro.ativo ? (
          canManage && (
            <button type="button" className="resp-ficha-btn resp-ficha-btn--danger" onClick={onDeactivate}>
              Desativar
            </button>
          )
        ) : (
          canManage && (
            <button type="button" className="resp-ficha-btn resp-ficha-btn--success" onClick={onReactivate}>
              Reativar
            </button>
          )
        )}
      </div>
    </div>
  );
}

export function EquipeFichaDesktop({
  membro,
  escolaId,
  canManage,
  initialTab = 'dados',
  onClose,
  onEdit,
  onDeactivate,
  onReactivate,
  onRefetch,
}: {
  membro: Record<string, unknown>;
  escolaId: string;
  canManage: boolean;
  initialTab?: 'dados' | 'convite';
  onClose: () => void;
  onEdit: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  onRefetch: () => void;
}) {
  const [tab, setTab] = useState<'dados' | 'convite'>(initialTab);
  const [gerando, setGerando] = useState(false);
  const [newActivationLink, setNewActivationLink] = useState<string | null>(null);

  const tokenExpiraEmRaw = membro.tokenExpiraEm as string | undefined;
  const statusConta = String(membro.statusConta ?? '');
  const tokenExpiraFmt = tokenExpiraEmRaw
    ? new Date(tokenExpiraEmRaw).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    : null;

  const role = resolveRole(membro as { perfil?: string; cargo?: string });
  const cargoLabel = PERFIL_TO_CARGO[role] || String(membro.cargo || '—');

  const permissoes = buildPermissionMap(
    role,
    cargoLabel,
    membro as { preferencias?: { permissoes?: PermissionMap } },
  );
  const activeCount = Object.values(permissoes).filter(Boolean).length;

  const fmtUltimo = membro.ultimoLogin
    ? new Date(String(membro.ultimoLogin)).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '—';

  return (
    <div className="resp-ficha">
      <header className="resp-ficha-head">
        <ListAvatar fotoUrl={membro.fotoUrl as string} nome={String(membro.nome)} size={48} />
        <div className="resp-ficha-head-main">
          <p className="resp-ficha-eyebrow">Equipe · ficha da escola</p>
          <h2 className="resp-ficha-name">{String(membro.nome)}</h2>
          <div className="resp-ficha-meta-line">
            <span className="resp-ficha-sub">{String(membro.email)}</span>
            <span className={`resp-ficha-badge ${membro.ativo ? 'resp-ficha-badge--ok' : 'resp-ficha-badge--off'}`}>
              {membro.ativo ? 'Ativo' : 'Inativo'}
            </span>
            <span className="resp-ficha-badge resp-ficha-badge--role">{cargoLabel}</span>
          </div>
        </div>
        <button type="button" className="resp-ficha-close" onClick={onClose} aria-label="Fechar">
          <X size={16} />
        </button>
      </header>

      <div className="resp-ficha-body">
        <div className="resp-ficha-stack">
          <div className="resp-ficha-kpis">
            <div className="resp-ficha-kpi">
              <span className="resp-ficha-kpi-icon"><Shield size={15} /></span>
              <div className="resp-ficha-kpi-main">
                <span className="resp-ficha-kpi-val">{statusConta || '—'}</span>
                <span className="resp-ficha-kpi-lbl">Status da conta</span>
              </div>
            </div>
            <div className="resp-ficha-kpi">
              <span className="resp-ficha-kpi-icon"><Shield size={15} /></span>
              <div className="resp-ficha-kpi-main">
                <span className="resp-ficha-kpi-val">{activeCount}</span>
                <span className="resp-ficha-kpi-lbl">Permissões ativas</span>
              </div>
            </div>
            <div className="resp-ficha-kpi">
              <span className="resp-ficha-kpi-icon"><Mail size={15} /></span>
              <div className="resp-ficha-kpi-main">
                <span className="resp-ficha-kpi-val" style={{ fontSize: '0.72rem' }}>{fmtUltimo}</span>
                <span className="resp-ficha-kpi-lbl">Último acesso</span>
              </div>
            </div>
          </div>

          <nav className="resp-ficha-tabs resp-ficha-tabs--scroll" aria-label="Secções da ficha">
            <button
              type="button"
              className={`resp-ficha-tab${tab === 'dados' ? ' resp-ficha-tab--active' : ''}`}
              onClick={() => setTab('dados')}
            >
              <User size={12} /> Dados
            </button>
            <button
              type="button"
              className={`resp-ficha-tab${tab === 'convite' ? ' resp-ficha-tab--active' : ''}`}
              onClick={() => setTab('convite')}
            >
              <Mail size={12} /> Convite
            </button>
          </nav>

          <div className="resp-ficha-panel">
            {tab === 'dados' && (
              <div className="resp-ficha-grid2">
                <div className="resp-ficha-card">
                  <h3 className="resp-ficha-card-title">
                    <User size={12} /> Informações do membro
                  </h3>
                  <dl className="resp-ficha-dl resp-ficha-dl--compact">
                    {[
                      ['Nome', String(membro.nome)],
                      ['E-mail', String(membro.email || '—')],
                      ['Telefone', String(membro.telefone || '—')],
                      ['Perfil', cargoLabel],
                      ['Cargo', String(membro.cargo || '—')],
                    ].map(([dt, dd]) => (
                      <div key={dt}>
                        <dt>{dt}</dt>
                        <dd>{dd}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
                <div className="resp-ficha-card">
                  <h3 className="resp-ficha-card-title">
                    <Shield size={12} /> Resumo de acessos
                  </h3>
                  <div className="resp-ficha-card-body resp-ficha-card-body--insights">
                    <p className="resp-ficha-insight">
                      <strong>{activeCount}</strong> permissões ativas para este perfil na escola.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                      {Object.entries(permissoes)
                        .filter(([, v]) => v)
                        .slice(0, 12)
                        .map(([k]) => (
                          <span
                            key={k}
                            style={{
                              fontSize: '0.62rem',
                              padding: '2px 6px',
                              borderRadius: 4,
                              background: '#f0fdf4',
                              color: '#166534',
                              border: '1px solid #bbf7d0',
                            }}
                          >
                            {k.replace('.view', '').replace('.manage', '')}
                          </span>
                        ))}
                      {activeCount > 12 && (
                        <span style={{ fontSize: '0.62rem', color: '#94a3b8' }}>+{activeCount - 12}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {tab === 'convite' && (
              <InviteStatusCard
                user={{
                  id: String(membro.id),
                  nome: String(membro.nome),
                  email: String(membro.email),
                  perfil: role,
                  telefone: (membro.telefone as string) || undefined,
                  statusConta: String(membro.statusConta ?? ''),
                  tokenAtivacao: (membro.tokenAtivacao as string) || undefined,
                  codigoDisplay: (membro.codigoDisplay as string) || undefined,
                  tokenExpiraEm: (membro.tokenExpiraEm as string) || undefined,
                  criadoEm: (membro.criadoEm as string) || undefined,
                  ultimoLogin: (membro.ultimoLogin as string) || undefined,
                }}
                onResend={async () => {
                  setGerando(true);
                  try {
                    const { data } = await api.post(ROUTES.equipe.convidar(escolaId), {
                      nome: membro.nome,
                      email: membro.email,
                      cargo: resolveRole(membro as { perfil?: string; cargo?: string }),
                      telefone: (membro.telefone as string) || undefined,
                    });
                    if (data.activationLink) {
                      setNewActivationLink(data.activationLink);
                      toast.success('Link renovado!');
                    }
                    onRefetch();
                  } catch {
                    toast.error('Erro ao gerar link');
                  } finally {
                    setGerando(false);
                  }
                }}
                resending={gerando}
                newActivationLink={newActivationLink}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
